<template>
  <section class="bg-t3-bg-dark py-16 sm:py-24 px-4 sm:px-6 lg:px-20 max-w-5xl mx-auto font-t3-display overflow-hidden" id="reviews" aria-labelledby="reviews-heading-t3">
    <div class="text-center mb-12 sm:mb-16">
      <h2 id="reviews-heading-t3" class="text-2xl sm:text-3xl font-bold text-slate-100">{{ $t('public.t3GuestExperiences') }}</h2>
    </div>
    <template v-if="feedbacks.length">
      <!-- Single feedback: static card -->
      <div v-if="feedbacks.length <= 1" class="flex flex-col items-center text-center px-4">
        <div class="flex gap-1 text-t3-primary mb-6" aria-hidden="true">
          <span
            v-for="s in 5"
            :key="s"
            class="material-icons text-2xl"
            :class="s > starCount(feedbacks[0]) ? 'text-slate-500' : ''"
          >{{ s <= starCount(feedbacks[0]) ? 'star' : 'star_border' }}</span>
        </div>
        <p class="font-t3-display text-xl sm:text-2xl md:text-3xl italic font-light text-slate-200 leading-snug mb-8 max-w-3xl">
          "{{ feedbacks[0].text || '' }}"
        </p>
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 rounded-full bg-t3-primary/20 flex items-center justify-center overflow-hidden shrink-0"
            aria-hidden="true"
          >
            <span class="text-sm font-semibold tracking-tight text-t3-primary select-none">{{ feedbackInitials(feedbacks[0].name) }}</span>
          </div>
          <div class="text-left">
            <h4 class="font-bold text-slate-100">{{ feedbacks[0].name || 'Anonymous' }}</h4>
          </div>
        </div>
      </div>
      <!-- Multiple: Swiper slider -->
      <div v-else class="relative">
        <Swiper
          :modules="swiperModules"
          effect="fade"
          :fade-effect="{ crossFade: true }"
          :slides-per-view="1"
          :space-between="0"
          :speed="reducedMotion ? 0 : 350"
          :autoplay="autoplayConfig"
          :pagination="paginationConfig"
          :a11y="a11yConfig"
          class="rms-t3-reviews-swiper min-h-[280px]"
          @swiper="onSwiper"
        >
          <SwiperSlide v-for="(fb, idx) in feedbacks" :key="idx">
            <div class="flex flex-col items-center text-center px-4 py-4" role="article">
              <div class="flex gap-1 text-t3-primary mb-6" aria-hidden="true">
                <span
                  v-for="s in 5"
                  :key="s"
                  class="material-icons text-2xl"
                  :class="s > starCount(fb) ? 'text-slate-500' : ''"
                >{{ s <= starCount(fb) ? 'star' : 'star_border' }}</span>
              </div>
              <p class="font-t3-display text-xl sm:text-2xl md:text-3xl italic font-light text-slate-200 leading-snug mb-8 max-w-3xl">
                "{{ fb.text || '' }}"
              </p>
              <div class="flex items-center gap-4">
                <div
                  class="w-12 h-12 rounded-full bg-t3-primary/20 flex items-center justify-center overflow-hidden shrink-0"
                  aria-hidden="true"
                >
                  <span class="text-sm font-semibold tracking-tight text-t3-primary select-none">{{ feedbackInitials(fb.name) }}</span>
                </div>
                <div class="text-left">
                  <h4 class="font-bold text-slate-100">{{ fb.name || 'Anonymous' }}</h4>
                </div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </template>
    <p v-else class="text-center text-slate-400 py-8">{{ emptyMessage }}</p>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectFade, Autoplay, Pagination, A11y } from 'swiper/modules'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { feedbackInitials } from '@/utils/feedbackInitials'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

const props = defineProps({
  feedbacks: { type: Array, default: () => [] },
  emptyMessage: { type: String, default: 'No reviews yet.' },
})

const reducedMotion = useReducedMotion()

const swiperModules = [EffectFade, Autoplay, Pagination, A11y]

const autoplayConfig = computed(() => {
  if (reducedMotion.value || props.feedbacks.length <= 1) return false
  return { delay: 5000, disableOnInteraction: false }
})

const paginationConfig = computed(() => {
  if (props.feedbacks.length <= 1) return false
  return {
    clickable: true,
    bulletClass: 'rms-t3-reviews__dot',
    bulletActiveClass: 'rms-t3-reviews__dot--active',
  }
})

const a11yConfig = computed(() => ({
  paginationBulletMessage: `Review {{index}} of ${props.feedbacks.length}`,
}))

function starCount(fb) {
  const r = Number(fb?.rating)
  return Math.min(5, Math.max(0, Number.isNaN(r) ? 0 : Math.round(r)))
}

function onSwiper(swiper) {
  if (swiper.pagination?.el) {
    swiper.pagination.el.setAttribute('role', 'tablist')
    swiper.pagination.el.setAttribute('aria-label', 'Review slides')
  }
  function setBulletA11y() {
    if (!swiper.pagination?.bullets) return
    swiper.pagination.bullets.forEach((bullet, i) => {
      bullet.setAttribute('role', 'tab')
      bullet.setAttribute('aria-selected', i === swiper.activeIndex ? 'true' : 'false')
    })
  }
  setBulletA11y()
  swiper.on('slideChange', setBulletA11y)
}
</script>

<style scoped>
.rms-t3-reviews-swiper :deep(.swiper-pagination) {
  position: relative;
  margin-top: 1.5rem;
}
.rms-t3-reviews-swiper :deep(.rms-t3-reviews__dot) {
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.3);
  opacity: 0.7;
  border-radius: 9999px;
  display: inline-block;
  margin: 0 5px;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;
}
.rms-t3-reviews-swiper :deep(.rms-t3-reviews__dot:hover),
.rms-t3-reviews-swiper :deep(.rms-t3-reviews__dot--active) {
  opacity: 1;
  background: var(--t3-primary, #f2b90d);
}
</style>
